import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Dumbbell, Leaf, ShoppingCart, Salad } from "lucide-react";
import api, { gbp, img } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const PackCard = ({ p, onBuy }) => (
  <div className="border border-white/10 bg-white/[0.03] flex flex-col hover:border-[#FF6B00]/60 transition-colors group" data-testid={`goals-pack-${p.id}`}>
    <Link to={`/product/${p.slug}`} className="h-32 overflow-hidden relative block">
      <img src={img(p.images?.[0], 480)} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" decoding="async" />
      <span className="absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 text-white flex items-center gap-1" style={{ background: p.kind === "programme" ? "#FF6B00" : "#00875A" }}>
        {p.kind === "programme" ? <Dumbbell className="w-3 h-3" /> : <Salad className="w-3 h-3" />}{p.kind === "programme" ? "8-week programme" : "7-day meal prep"}
      </span>
      {p.vegan && <span className="absolute top-2 right-2 bg-[#00875A] text-white text-[10px] font-bold px-2 py-0.5 flex items-center gap-1" data-testid={`goals-vegan-${p.id}`}><Leaf className="w-3 h-3" />Vegan</span>}
    </Link>
    <div className="p-3 flex flex-col flex-1">
      <h3 className="text-white text-sm font-medium leading-snug flex-1 line-clamp-2">{p.title.split(" — ")[0]}</h3>
      <div className="flex items-center justify-between mt-3">
        <span className="font-heading text-lg text-white">{gbp(p.price)}</span>
        <button onClick={() => onBuy(p)} className="bg-[#FF6B00] text-white px-3 py-1.5 text-xs font-medium hover:bg-white hover:text-black transition-colors flex items-center gap-1" data-testid={`goals-buy-${p.id}`}><ShoppingCart className="w-3 h-3" />Add</button>
      </div>
    </div>
  </div>
);

export const BodyGoals = () => {
  const [data, setData] = useState(null);
  const [goal, setGoal] = useState("all");
  const [vegan, setVegan] = useState(false);
  const { addToCart } = useCart();
  useEffect(() => { api.get("/fitness-packs").then((r) => setData(r.data)).catch(() => {}); }, []);
  const shown = useMemo(() => (data?.products || []).filter((p) => (goal === "all" || p.goal === goal) && (!vegan || p.vegan)), [data, goal, vegan]);
  if (!data || !data.products.length) return null;
  const buy = (p) => { addToCart(p); toast.success(`${p.title.split(" — ")[0]} added to basket`); };
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-16" data-testid="home-body-goals">
      <div className="bg-[#0A0A0A] border border-white/10 p-8 md:p-10">
        <div className="flex flex-col md:flex-row md:items-end gap-6 mb-6">
          <div className="flex-1">
            <span className="label-caps text-[#FF6B00] flex items-center gap-2"><Dumbbell className="w-4 h-4" />MRQK Fit · Body goals</span>
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-medium mt-1 mb-2 text-white">Pick a goal. Get the plan and the programme.</h2>
            <p className="text-neutral-400 max-w-2xl">Meal-prep plans and 8-week gym programmes written the way good coaches actually work — with vegan options, UK shopping lists and no 1,200-calorie nonsense. Instant PDF download.</p>
          </div>
          <Link to="/workplace-documents?cat=fitness" className="text-sm text-[#FF6B00] hover:text-white transition-colors flex items-center gap-1.5 whitespace-nowrap" data-testid="goals-see-all">See all fitness packs <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {data.goals.map(([k, l]) => (
            <button key={k} onClick={() => setGoal(k)} data-testid={`goals-chip-${k}`}
              className={`px-3.5 py-1.5 text-xs font-medium border transition-colors ${goal === k ? "bg-white text-black border-white" : "border-white/20 text-neutral-300 hover:border-white"}`}>{l}</button>
          ))}
          <button onClick={() => setVegan(!vegan)} data-testid="goals-vegan-toggle" aria-pressed={vegan}
            className={`ml-auto px-3.5 py-1.5 text-xs font-medium border flex items-center gap-1.5 transition-colors ${vegan ? "bg-[#00875A] text-white border-[#00875A]" : "border-white/20 text-neutral-300 hover:border-[#00875A]"}`}><Leaf className="w-3.5 h-3.5" />Vegan only</button>
        </div>
        {shown.length === 0 ? (
          <p className="text-sm text-neutral-400 border border-dashed border-white/15 p-8 text-center" data-testid="goals-empty">No vegan pack for that goal yet — the vegan Fat Loss and Muscle Gain plans pair with any programme.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="goals-grid">{shown.map((p) => <PackCard key={p.id} p={p} onBuy={buy} />)}</div>
        )}
        {data.bundles.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="goals-bundles">
            {data.bundles.map((b) => (
              <div key={b.id} className="border border-[#FF6B00]/40 bg-[#FF6B00]/[0.06] p-4 flex items-center gap-4" data-testid={`goals-bundle-${b.id}`}>
                <img src={img(b.images?.[0], 240)} alt="" className="w-16 h-16 object-cover shrink-0" loading="lazy" decoding="async" />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium leading-snug line-clamp-2">{b.title.split(" — ")[0]}</p>
                  <p className="text-xs text-neutral-400 mt-0.5"><span className="text-white font-semibold">{gbp(b.price)}</span> <span className="line-through ml-1">{gbp(b.compare_at_price)}</span></p>
                </div>
                <button onClick={() => buy(b)} className="border border-white/30 text-white px-3 py-1.5 text-xs hover:bg-white hover:text-black transition-colors" data-testid={`goals-bundle-buy-${b.id}`}>Add</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
